import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Skeleton } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';

import { AuthService } from '../../core/services/auth.service';
import { ContactoService } from '../../core/services/contacto.service';
import { IglesiasService } from '../../core/services/iglesias.service';
import { UserStore } from '../../core/services/user-store.service';
import { ContentCardComponent } from '../../shared/components/content-card/content-card.component';
import { ContactoListado } from '../../shared/interfaces/contacto.interface';
import { IglesiaProfile } from '../../shared/interfaces/iglesia.interface';

@Component({
  selector: 'app-superpanel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    Button,
    Dialog,
    InputText,
    TableModule,
    Skeleton,
    Toast,
    ContentCardComponent,
    DatePipe,
  ],
  templateUrl: './superpanel.component.html',
})
export class SuperpanelComponent implements OnInit {
  readonly skeletonRows = [0, 1, 2, 3, 4, 5];
  private readonly iglesiasService = inject(IglesiasService);
  private readonly contactoService = inject(ContactoService);
  private readonly auth = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly messages = inject(MessageService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly iglesias = signal<IglesiaProfile[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly contactos = signal<ContactoListado[]>([]);
  readonly contactosLoading = signal(true);
  readonly contactosError = signal(false);
  readonly dialogoAbierto = signal(false);
  readonly guardando = signal(false);
  readonly cambiando = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    nombreIglesia: ['', Validators.required],
    nombrePastor: ['', Validators.required],
    celular: [''],
    direccion: [''],
    ciudad: [''],
  });

  ngOnInit(): void {
    this.cargar();
    this.cargarContactos();
  }

  activa(iglesia: IglesiaProfile): boolean {
    return iglesia.svIglesia && !iglesia.suspender;
  }

  mailAdmin(): string {
    return mailAdminInicial(this.form.controls.nombreIglesia.value);
  }

  viendo(nombreIglesia: string): boolean {
    return this.userStore.vistaIglesia() === nombreIglesia;
  }

  entrar(nombreIglesia: string): void {
    this.cambiando.set(nombreIglesia);
    this.iglesiasService.entrar(nombreIglesia).subscribe({
      next: (response) => {
        this.auth.reemplazarSesion(response.data, nombreIglesia);
        this.cambiando.set(null);
        this.messages.add({
          severity: 'success',
          summary: nombreIglesia,
          detail: 'Podés recorrer los menús como admin, líder y contador de esta iglesia.',
        });
      },
      error: (error: HttpErrorResponse) => {
        this.cambiando.set(null);
        this.messages.add({
          severity: 'error',
          summary: 'No se pudo entrar',
          detail: this.detalleError(error),
        });
      },
    });
  }

  salir(): void {
    const actual = this.userStore.vistaIglesia();
    this.cambiando.set(actual);
    this.iglesiasService.salir().subscribe({
      next: (response) => {
        this.auth.reemplazarSesion(response.data, null);
        this.cambiando.set(null);
        this.messages.add({
          severity: 'success',
          summary: 'Listo',
          detail: 'Volviste a tu sesión.',
        });
      },
      error: (error: HttpErrorResponse) => {
        this.cambiando.set(null);
        this.messages.add({
          severity: 'error',
          summary: 'No se pudo salir',
          detail: this.detalleError(error),
        });
      },
    });
  }

  verGraficos(nombreIglesia: string): void {
    void this.router.navigate(['/graficos'], { queryParams: { iglesia: nombreIglesia } });
  }

  abrirRegistro(): void {
    this.form.reset();
    this.dialogoAbierto.set(true);
  }

  cerrarRegistro(): void {
    if (this.guardando()) {
      return;
    }
    this.dialogoAbierto.set(false);
  }

  registrar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    if (!mailAdminInicial(value.nombreIglesia)) {
      this.messages.add({
        severity: 'error',
        summary: 'No se pudo registrar',
        detail: 'El nombre de la iglesia no sirve para armar el mail del administrador.',
      });
      return;
    }

    this.guardando.set(true);
    this.iglesiasService
      .registrar({
        nombreIglesia: value.nombreIglesia.trim(),
        nombrePastor: value.nombrePastor.trim(),
        celular: value.celular.trim(),
        direccion: value.direccion.trim(),
        ciudad: value.ciudad.trim(),
      })
      .subscribe({
        next: (response) => {
          this.guardando.set(false);
          this.dialogoAbierto.set(false);
          this.messages.add({
            severity: 'success',
            summary: 'Iglesia registrada',
            detail: `Entrá con ${response.adminMail} y la contraseña en blanco.`,
          });
          this.cargar();
        },
        error: (error: HttpErrorResponse) => {
          this.guardando.set(false);
          this.messages.add({
            severity: 'error',
            summary: 'No se pudo registrar',
            detail: this.detalleError(error),
          });
        },
      });
  }

  private cargar(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.iglesiasService.listTodas().subscribe({
      next: (iglesias) => {
        this.iglesias.set(iglesias);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set(true);
      },
    });
  }

  private cargarContactos(): void {
    this.contactosLoading.set(true);
    this.contactosError.set(false);
    this.contactoService.listar().subscribe({
      next: (response) => {
        this.contactos.set(response.data);
        this.contactosLoading.set(false);
      },
      error: () => {
        this.contactosLoading.set(false);
        this.contactosError.set(true);
      },
    });
  }

  private detalleError(error: HttpErrorResponse): string {
    const message = error.error?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return 'Revisá los datos e intentá de nuevo.';
  }
}

function mailAdminInicial(nombreIglesia: string): string {
  const dominio = nombreIglesia
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  return dominio ? `admin@${dominio}.com` : '';
}
